export interface ApiTestParam {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
}

export interface ApiTestBodyField {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: unknown;
}

export interface ApiTestEndpoint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  category: string;
  requiresAuth: boolean;
  requiresApiKey: boolean;
  /** Resolved from session, API key, or impersonation — not a query/path param. */
  orgScoped?: boolean;
  /**
   * Which plans' API keys may call this endpoint. 'pro' = Pro and above
   * (Starter keys get 403), 'all' = every paid plan, 'public' = no key needed,
   * 'session' = dashboard login only. Unset = not plan-gated.
   */
  plan?: 'public' | 'all' | 'pro' | 'session';
  parameters?: ApiTestParam[];
  body?: ApiTestBodyField[];
}

/** Key management is a logged-in dashboard action, not a store API-key grant. */
export function isSessionOnlyApiPath(path: string): boolean {
  return path === '/api-keys' || path.startsWith('/api-keys/');
}

/**
 * Headers for generated curl/JS/Python examples.
 * Store-facing routes document X-API-Key. Session-only routes use Bearer ID token.
 */
export function snippetAuthHeaders(
  endpoint: Pick<ApiTestEndpoint, 'path' | 'requiresAuth' | 'requiresApiKey'>
): Record<string, string> {
  if (!endpoint.requiresAuth && !endpoint.requiresApiKey) {
    return {};
  }
  if (isSessionOnlyApiPath(endpoint.path)) {
    return { Authorization: 'Bearer <YOUR_ID_TOKEN>' };
  }
  return { 'X-API-Key': '<YOUR_API_KEY>' };
}

export interface RequestDetails {
  relativeUrl: string;
  pathParamNames: string[];
  query: Record<string, string>;
  parsedBody: unknown;
}

export type SubmissionLocation = 'path' | 'query' | 'body' | 'header' | 'context';

export interface SubmissionField {
  name: string;
  value: string;
  location: SubmissionLocation;
  required?: boolean;
  description?: string;
  missing?: boolean;
}

export interface RequestContext {
  organizationId?: string | null;
  organizationName?: string | null;
  impersonating?: boolean;
  apiKey?: string | null;
  hasSession?: boolean;
}

export function maskSecret(value: string | null | undefined, visible = 4): string {
  if (!value) return '';
  if (value.length <= visible * 2) return '••••';
  return `${value.slice(0, visible)}…${value.slice(-visible)}`;
}

export function pathParamNames(path: string): string[] {
  const names: string[] = [];
  const regex = /:([a-zA-Z0-9_]+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(path)) !== null) {
    names.push(match[1]);
  }
  return names;
}

export function buildRequestDetails(
  endpoint: Pick<ApiTestEndpoint, 'path' | 'method' | 'parameters'>,
  paramValues: Record<string, string>,
  bodyText: string
): RequestDetails {
  const names = pathParamNames(endpoint.path);
  const pathParamSet = new Set(names);
  let relativePath = endpoint.path;

  names.forEach((paramName) => {
    const value = paramValues[paramName] ?? paramValues[`:${paramName}`];
    if (value) {
      relativePath = relativePath.replace(`:${paramName}`, encodeURIComponent(value));
    }
  });

  const query: Record<string, string> = {};
  if (endpoint.parameters) {
    endpoint.parameters.forEach((param) => {
      const paramName = param.name.replace(/^:/, '');
      const value = paramValues[paramName];
      if (!pathParamSet.has(paramName) && value) {
        query[paramName] = value;
      }
    });
  }

  const queryString = new URLSearchParams(query).toString();
  const relativeUrl = queryString ? `${relativePath}?${queryString}` : relativePath;

  let parsedBody: unknown;
  if (bodyText && bodyText.trim().length > 0 && endpoint.method !== 'GET') {
    try {
      parsedBody = JSON.parse(bodyText);
    } catch {
      parsedBody = bodyText;
    }
  }

  return {
    relativeUrl,
    pathParamNames: names,
    query,
    parsedBody,
  };
}

export function buildSubmissionFields(
  endpoint: ApiTestEndpoint,
  paramValues: Record<string, string>,
  bodyText: string,
  context: RequestContext = {}
): SubmissionField[] {
  const details = buildRequestDetails(endpoint, paramValues, bodyText);
  const pathSet = new Set(details.pathParamNames);
  const fields: SubmissionField[] = [];

  if (endpoint.parameters) {
    endpoint.parameters.forEach((param) => {
      const paramName = param.name.replace(/^:/, '');
      const value = paramValues[paramName] || '';
      const location: SubmissionLocation = pathSet.has(paramName) ? 'path' : 'query';
      fields.push({
        name: paramName,
        value,
        location,
        required: param.required,
        description: param.description,
        missing: param.required && !value,
      });
    });
  }

  if (endpoint.orgScoped) {
    const orgId = context.organizationId || '';
    const namePart = context.organizationName ? ` (${context.organizationName})` : '';
    const source = context.impersonating
      ? 'From impersonation header X-Impersonated-Organization-Id — not a query param.'
      : 'Resolved from your session or API key organization — not a query param.';
    fields.push({
      name: 'organizationId',
      value: orgId ? `${orgId}${namePart}` : '',
      location: 'context',
      required: true,
      description: source,
      missing: !orgId,
    });
  }

  if (endpoint.requiresApiKey || endpoint.requiresAuth) {
    if (context.apiKey) {
      fields.push({
        name: 'X-API-Key',
        value: maskSecret(context.apiKey),
        location: 'header',
        description: 'Sent on this test request',
      });
    }
    if (context.hasSession) {
      fields.push({
        name: 'Authorization',
        value: 'Bearer (logged-in session)',
        location: 'header',
        description: 'Firebase ID token for this browser session',
      });
    }
    if (context.impersonating && context.organizationId) {
      fields.push({
        name: 'X-Impersonated-Organization-Id',
        value: context.organizationId,
        location: 'header',
        description: 'Admin impersonation of a store or vendor org',
      });
    }
  }

  if (details.parsedBody !== undefined && endpoint.method !== 'GET') {
    const preview =
      typeof details.parsedBody === 'string'
        ? details.parsedBody
        : JSON.stringify(details.parsedBody);
    fields.push({
      name: 'body',
      value: preview,
      location: 'body',
      description: 'JSON request body',
    });
  }

  return fields;
}
